import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";

const LOGO_URL = "/logo-prenova.png";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();

    // Hide on admin portals
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/adminrenova")) {
        return null;
    }

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const slugMatch = location.pathname.match(/^\/r\/([^/]+)/);
    const slug = slugMatch ? slugMatch[1] : null;

    return (
        <header className="fixed top-0 left-0 right-0 z-50 glass-panel" data-testid="main-navbar">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <Link to={slug ? `/r/${slug}` : "/"} className="flex items-center gap-3" data-testid="navbar-logo-link">
                    <div className="h-12 sm:h-14 w-auto flex items-center">
                        <img src={LOGO_URL} alt="Prenova by Renova360" className="h-full w-auto object-contain" />
                    </div>
                    <div className="hidden lg:flex flex-col leading-tight border-l border-gold/30 pl-3">
                        <span className="text-[10px] text-gold tracking-[0.3em] uppercase">{t("navbar.tagline")}</span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-7">
                    <Link to="/" className="text-sm text-gray-300 hover:text-gold transition-colors" data-testid="nav-home">{t("common.home")}</Link>
                    {slug && (
                        <Link to={`/r/${slug}/book`} className="text-sm text-gray-300 hover:text-gold transition-colors" data-testid="nav-book">
                            {t("common.book")}
                        </Link>
                    )}
                    {user && (
                        <Link to="/my-reservations" className="text-sm text-gray-300 hover:text-gold transition-colors" data-testid="nav-my-reservations">
                            {t("book.my_reservations")}
                        </Link>
                    )}
                    <LanguageSelector />
                    {user ? (
                        <div className="flex items-center gap-3 pl-4 border-l border-white/10">
                            {user.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />}
                            <span className="text-xs text-gray-400 max-w-[120px] truncate">{user.name}</span>
                            <button onClick={handleLogout} className="text-gray-400 hover:text-gold transition-colors" data-testid="nav-logout-btn" aria-label={t("common.logout")}>
                                <LogOut size={16} strokeWidth={1.5} />
                            </button>
                        </div>
                    ) : (
                        <Link to="/login" className="bg-gold text-black font-semibold rounded-sm px-5 py-2 hover:bg-gold-light transition-colors text-sm tracking-wide" data-testid="nav-login-btn">
                            {t("common.login")}
                        </Link>
                    )}
                </nav>

                <div className="md:hidden flex items-center gap-2">
                    <LanguageSelector compact />
                    <button className="text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden border-t border-white/10 bg-ink-surface px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
                    <Link to="/" onClick={() => setOpen(false)} className="text-gray-300 hover:text-gold">{t("common.home")}</Link>
                    {slug && <Link to={`/r/${slug}/book`} onClick={() => setOpen(false)} className="text-gray-300 hover:text-gold">{t("common.book")}</Link>}
                    {user && <Link to="/my-reservations" onClick={() => setOpen(false)} className="text-gray-300 hover:text-gold">{t("book.my_reservations")}</Link>}
                    {user ? (
                        <button onClick={handleLogout} className="flex items-center gap-2 text-gold"><LogOut size={16} /> {t("common.logout")}</button>
                    ) : (
                        <Link to="/login" onClick={() => setOpen(false)} className="bg-gold text-black px-4 py-2 rounded-sm text-center">{t("common.login")}</Link>
                    )}
                </div>
            )}
        </header>
    );
}
